import {
  deriveItemCardStateId,
  LISTING_FORMATS,
  type ItemCardLifecycle,
  type ItemCardListingDiscriminator,
  type ItemCardStateId,
  type ItemCardViewer,
  type ListingFormat,
} from "@kraahl/contract";

import { itemCard } from "@/lib/item-card-tokens";
import { isListingDropPending, itemCardStatusFromListing } from "@/lib/item-card-status";
import { getBcp47Locale } from "@/lib/i18n/locale-utils";

/**
 * Re-export the canonical `ItemCardStateId` so the historical
 * `import { ItemCardStateId } from "@/lib/build-item-card-view"` path
 * (notably `components/search/item-card.tsx`) keeps working. Canonical
 * declaration lives in `@kraahl/contract/item-card-state.ts` (Phase 7 of
 * `shippo-listing-discriminator`). No local literal-union declaration here.
 */
export type { ItemCardStateId };

export type ItemCardCtaIcon = "bag" | "gavel" | "offer" | "bell" | "search" | "pencil";

/**
 * Visual treatment of the card CTA (the DA "base + foil" model). The domain
 * decides tone, not the component: a possible commerce action is `foil`, a
 * management action (edit / find-similar) is `ink`, an unavailable/disabled
 * state is `muted`. See .tondocs/ui/FOIL.md.
 */
export type ItemCardCtaTone = "foil" | "ink" | "muted";

export type ItemCardViewModel = {
  stateId: ItemCardStateId;
  /**
   * Blur the cover as a scheduled-drop teaser. TRUE only while the drop is
   * still pending (`scheduled_publish_at` in the future) — NOT for the whole
   * life of a `stateId === "preorder"` card. A pre-order that has dropped (or
   * dropped then sold out) keeps `stateId === "preorder"` but is revealed, so
   * blur must be gated on this field, not on the stateId (bug: live/closed
   * pre-orders stayed blurred on the feed & profile).
   */
  blurImage: boolean;
  pillText: string | null;
  pillMode: "urgent" | "soft" | "none";
  pillAccentColor?: string;
  priceSub: string | null;
  ctaLabel: string | null;
  ctaIcon: ItemCardCtaIcon | null;
  ctaTone: ItemCardCtaTone;
  ctaDisabled: boolean;
  goneHead: string;
  goneSub: string;
};

type ListingInput = {
  scheduled_publish_at?: string | null;
  listing_format?: string | null;
  listing_type?: string | null;
  discoverTypeLabel?: string | null;
  quantity?: number;
  auction_status?: string | null;
  offers_pending_count?: number | null;
  auction_bid_count?: number | null;
  scheduledPublishAt?: string | null;
};

type BuildArgs = {
  listing: ListingInput;
  isOwner: boolean;
  auctionCountdown: string | null;
  preorderCountdown: string | null;
  notifyDone: boolean;
  showCta: boolean;
  t: (key: string, values?: Record<string, string | number>) => string;
  locale: string;
};

function formatOfferCount(count: number, t: BuildArgs["t"]): string {
  if (count === 1) return t("offerCountOne");
  return t("offerCount", { count });
}

function formatBidCount(count: number, t: BuildArgs["t"]): string {
  if (count === 1) return t("bidCountOne");
  return t("bidCount", { count });
}

function formatScheduledDrop(
  scheduledAt: string | null | undefined,
  locale: string,
): string | null {
  if (!scheduledAt) return null;
  const d = new Date(scheduledAt);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(getBcp47Locale(locale), {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const CANONICAL_FORMATS: ReadonlySet<ListingFormat> = new Set(LISTING_FORMATS);

/**
 * Narrow a raw row-string into the canonical `ListingFormat` union, falling
 * back to `"fixed_price"` for unknown / missing values. The presentation
 * seam is downstream of the ADT parser, but `ListingInput` is still a raw
 * row-ish shape — this is the local guard that keeps the canonical helper's
 * exhaustive switch typesafe.
 */
function narrowListingFormat(raw: string | null | undefined): ListingFormat {
  if (raw && CANONICAL_FORMATS.has(raw as ListingFormat)) {
    return raw as ListingFormat;
  }
  if (raw) {
    console.warn(
      `[build-item-card-view] unknown listing_format ${JSON.stringify(raw)} — collapsing to "fixed_price". A new format was added to the DB without updating the canonical LISTING_FORMATS tuple in @kraahl/contract.`,
    );
  }
  return "fixed_price";
}

export function buildItemCardView(args: BuildArgs): ItemCardViewModel {
  const {
    listing,
    isOwner,
    auctionCountdown,
    preorderCountdown,
    notifyDone,
    showCta,
    t,
    locale,
  } = args;

  const scheduledAt = listing.scheduled_publish_at ?? listing.scheduledPublishAt ?? null;
  const status = itemCardStatusFromListing({
    scheduled_publish_at: scheduledAt,
    listing_format: listing.listing_format ?? null,
    listing_type: listing.listing_type ?? null,
    discoverTypeLabel: listing.discoverTypeLabel ?? null,
  });
  const dropPending = isListingDropPending({ scheduled_publish_at: scheduledAt });
  const showPreorderState = dropPending && (status === "scheduled" || status === "pre-order");
  const auctionStatusRaw = String(listing.auction_status ?? "").toLowerCase();
  const isAuctionEnded = auctionStatusRaw === "ended" || auctionStatusRaw === "settled";
  const isOutOfStock = typeof listing.quantity === "number" && listing.quantity <= 0;
  const isPreorderType = listing.listing_type === "preorder";
  const isPreorderClosed = isPreorderType && isOutOfStock;
  const offerCount = listing.offers_pending_count ?? 0;
  const bidCount = listing.auction_bid_count ?? 0;

  // Canonical state derivation — single SoT in @kraahl/contract.
  // Both web (this file) and mobile (apps/mobile/lib/build-listing-item-card-view.ts)
  // call deriveItemCardStateId — no parallel if/else chains.
  const format = narrowListingFormat(listing.listing_format);
  const discriminator: ItemCardListingDiscriminator = {
    format,
    type: isPreorderType ? "preorder" : "standard",
  };
  const viewer: ItemCardViewer = { isOwner };
  const lifecycle: ItemCardLifecycle = {
    // Sold = stock-exhausted on non-auction (excluding preorder, which has
    // its own UI variant) OR auction ended/settled.
    isSold:
      (format === "auction" && isAuctionEnded) ||
      (format !== "auction" && isOutOfStock && !isPreorderType),
    isPreorderClosed,
    isScheduledDrop: showPreorderState,
  };
  const stateId: ItemCardStateId = deriveItemCardStateId(
    discriminator,
    viewer,
    lifecycle,
  );

  let pillMode: ItemCardViewModel["pillMode"] = "none";
  let pillText: string | null = null;

  if (stateId === "auction" && auctionCountdown) {
    pillMode = "urgent";
    pillText = auctionCountdown;
  } else if (stateId === "offers" && offerCount >= 1) {
    pillMode = "urgent";
    pillText = formatOfferCount(offerCount, t);
  } else if (stateId === "preorder") {
    // "Scheduled" only makes sense while the drop is still ahead. A pre-order
    // that already went live and then sold out keeps stateId "preorder" (so the
    // "Pre-orders closed" sub still shows) but is no longer scheduled — its drop
    // date is in the past, so there is no live countdown. In that case show no
    // pill rather than falling back to a stale "Scheduled" badge.
    if (preorderCountdown) {
      // Show just the countdown ("01h 55m"). The full drop date is already on the
      // price-sub line, and a long "Drops in …" string would collide with the
      // bookmark button on narrow cards.
      pillMode = "urgent";
      pillText = preorderCountdown;
    } else if (showPreorderState) {
      // Drop still pending but the countdown hasn't been computed yet — keep the label.
      pillMode = "urgent";
      pillText = t("scheduledPill");
    }
  }

  let priceSub: string | null = null;
  if (stateId === "auction" && bidCount > 0) {
    priceSub = formatBidCount(bidCount, t);
  } else if (stateId === "preorder") {
    if (isPreorderClosed) {
      priceSub = t("preordersClosed");
    } else if (showPreorderState) {
      // Only while the drop is still ahead. A pre-order that already dropped
      // keeps stateId "preorder" but is now live/buyable — showing its past
      // drop date reads as "not yet available". Mirror of the pill guard above.
      priceSub = formatScheduledDrop(scheduledAt, locale);
    }
  }

  let ctaLabel: string | null = null;
  let ctaIcon: ItemCardCtaIcon | null = null;
  let ctaTone: ItemCardCtaTone = "foil";
  let ctaDisabled = false;

  if (showCta) {
    if (stateId === "sold" && isOwner) {
      ctaLabel = t("ctaEdit");
      ctaIcon = "pencil";
      ctaTone = "ink";
    } else if (stateId === "sold") {
      ctaLabel = t("ctaFindSimilar");
      ctaIcon = "search";
      ctaTone = "ink";
    } else if (stateId === "own") {
      ctaLabel = t("ctaEdit");
      ctaIcon = "pencil";
      ctaTone = "ink";
    } else if (stateId === "preorder" && isPreorderClosed) {
      ctaLabel = t("preordersClosed");
      ctaDisabled = true;
      ctaTone = "muted";
    } else if (showPreorderState) {
      ctaLabel = notifyDone ? t("subscribed") : t("notify");
      ctaIcon = "bell";
      ctaDisabled = notifyDone;
      // Reminder is a possible action → foil; once set it leaves foil (muted).
      ctaTone = notifyDone ? "muted" : "foil";
    } else if (status === "auction" && !isAuctionEnded) {
      ctaLabel = t("bid");
      ctaIcon = "gavel";
      ctaTone = "foil";
    } else if (status === "offers") {
      ctaLabel = t("offer");
      ctaIcon = "offer";
      ctaTone = "foil";
    } else if (isOutOfStock) {
      ctaLabel = t("soldOut");
      ctaDisabled = true;
      ctaTone = "muted";
    } else {
      ctaLabel = t("buy");
      ctaIcon = "bag";
      ctaTone = "foil";
    }
  }

  return {
    stateId,
    blurImage: showPreorderState,
    pillText,
    pillMode,
    priceSub,
    ctaLabel,
    ctaIcon,
    ctaTone,
    ctaDisabled,
    goneHead: t("goneHead"),
    goneSub: t("goneSub"),
    pillAccentColor: stateId === "own" ? itemCard.stateAccent.own : undefined,
  };
}
